import axios, { AxiosResponse } from 'axios';
import { getAccessToken } from './accessToken';
import { URL_LIBRANZAS, LIBRANZAS_APIKEY } from '@/constants/api-constants';

const DOC_TYPE_MAP: Record<string, string> = {
    CO1C: '1',
    CO1E: '2',
    CO1N: '3',
    TI: '4',
    PA: '5',
    PEP: '6',
    PPT: '7',
};

const CACHE_TTL_MS = 5 * 60 * 1000;

type CacheEntry = { data: AxiosResponse; timestamp: number };
const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<AxiosResponse>>();

const buildCacheKey = (tipo: string, numero: string) => `${tipo}-${numero}`;

const isServerError = (error: any): boolean =>
    typeof error?.response?.status === 'number' && error.response.status >= 500;

const requestWithRetry = (token: string, tipoDeIdentificacion: string, numeroDeIdentificacion: string): Promise<AxiosResponse> => {
    const call = () =>
        axios.request({
            method: 'post',
            url: `${URL_LIBRANZAS}/consulta-deudores`,
            headers: {
                apikey: LIBRANZAS_APIKEY,
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            data: { tipoDeIdentificacion, numeroDeIdentificacion },
        });

    return call().catch((error) => {
        if (isServerError(error)) return call();
        return Promise.reject(error);
    });
};

export const getLibranzasEmpresas = (tipoDeIdentificacion: string, numeroDeIdentificacion: string): Promise<AxiosResponse> => {
    const cacheKey = buildCacheKey(tipoDeIdentificacion, numeroDeIdentificacion);

    const cached = cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
        return Promise.resolve(cached.data);
    }

    const existing = inFlight.get(cacheKey);
    if (existing) return existing;

    const tipoDeIdentificacionId = DOC_TYPE_MAP[tipoDeIdentificacion] ?? tipoDeIdentificacion;

    const request = getAccessToken()
        .then((tokenResponse) => {
            const token = tokenResponse?.data.data.id_token;
            return requestWithRetry(token, tipoDeIdentificacionId, numeroDeIdentificacion);
        })
        .then((result) => {
            if (result.status === 200) {
                cache.set(cacheKey, { data: result, timestamp: Date.now() });
            }
            return result;
        })
        .catch((error) => {
            console.error(error);
            throw error;
        })
        .finally(() => {
            inFlight.delete(cacheKey);
        });

    inFlight.set(cacheKey, request);
    return request;
};

export const clearLibranzasCache = (tipoDeIdentificacion?: string, numeroDeIdentificacion?: string) => {
    if (tipoDeIdentificacion && numeroDeIdentificacion) {
        cache.delete(buildCacheKey(tipoDeIdentificacion, numeroDeIdentificacion));
    } else {
        cache.clear();
    }
};
